import * as React from "react";

import { ServiceItem } from "../../../calcBusterTypes";
import {
  Badge,
  Box,
  CollectionPreferences,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import { USDollar } from "../../../util";

type ServiceData = {
  serviceCode: string;
  eligibility: string;
  region: string;
  serviceCost: number;
};
type SortingField = keyof ServiceData;

const EligibleServicesTable: React.FC<{ services: ServiceItem[] }> = ({
  services,
}) => {
  const [filteringText, setFilteringText] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [preferences, setPreferences] = React.useState({ pageSize: 10 });
  const [sortingField, setSortingField] =
    React.useState<SortingField>("serviceCost");
  const [sortingDescending, setSortingDescending] = React.useState(true);

  // Helper function to get eligibility string for sorting and filtering
  const getEligibilityString = (item: ServiceItem) => {
    const eligibilities = [];
    if (item.mapEligible) eligibilities.push("MAP Eligible");
    if (item.mapSapAndOracleEligible) eligibilities.push("SAP/Oracle");
    if (item.mapDBAndAnalyticsEligible) eligibilities.push("DB/Analytics");
    if (item.isModernService) eligibilities.push("Modern Service");
    return eligibilities.length ? eligibilities.join(", ") : "Not Eligible";
  };

  const processedData = React.useMemo(() => {
    return services.reduce((accumulator, parentService) => {
      if (parentService.subServices?.length) {
        const processedSubServices = parentService.subServices.map(
          (subService) => ({
            ...subService,
            serviceCode: `${subService.serviceCode} (${parentService.serviceCode})`,
          })
        );
        return [...accumulator, ...processedSubServices];
      }
      return [...accumulator, parentService];
    }, [] as ServiceItem[]);
  }, [services]);

  // Filter items based on text input
  const filteredItems = React.useMemo(() => {
    if (!filteringText) return processedData;

    return processedData.filter(
      (item) =>
        item.serviceCode?.toLowerCase().includes(filteringText.toLowerCase()) ||
        item.description?.toLowerCase().includes(filteringText.toLowerCase()) ||
        getEligibilityString(item)
          .toLowerCase()
          .includes(filteringText.toLowerCase())
    );
  }, [processedData, filteringText]);

  // Sort items
  const sortedItems = React.useMemo(() => {
    if (!sortingField) return filteredItems;

    const comparisonFn = (a: ServiceItem, b: ServiceItem) => {
      if (sortingField === "eligibility") {
        const comparison = getEligibilityString(a).localeCompare(
          getEligibilityString(b)
        );
        return sortingDescending ? -comparison : comparison;
      }

      const aValue =
        sortingField === "serviceCost"
          ? a[sortingField] || 0
          : a[sortingField] || "";
      const bValue =
        sortingField === "serviceCost"
          ? b[sortingField] || 0
          : b[sortingField] || "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortingDescending ? bValue - aValue : aValue - bValue;
      }

      const comparison = String(aValue).localeCompare(String(bValue));
      return sortingDescending ? -comparison : comparison;
    }

    const sortedItems = [...filteredItems].sort(comparisonFn)

    return sortedItems;
  }, [filteredItems, sortingField, sortingDescending]);

  // Apply pagination
  const paginatedItems = React.useMemo(() => {
    const startIndex = (currentPage - 1) * preferences.pageSize;
    const endIndex = startIndex + preferences.pageSize;
    return sortedItems.slice(startIndex, endIndex);
  }, [sortedItems, currentPage, preferences.pageSize]);

  // Table column definitions
  const columnDefinitions = [
    {
      id: "serviceCode",
      header: "Service",
      cell: (item: ServiceItem) => item.serviceCode,
      sortingField: "serviceCode",
    },
    {
      id: "eligibility",
      header: "Eligibility",
      cell: (item: ServiceItem) => (
        <SpaceBetween direction="vertical" size="xs">
          {item.mapEligible && <Badge color="blue">MAP Eligible</Badge>}
          {item.mapSapAndOracleEligible && (
            <Badge color="red">SAP/Oracle</Badge>
          )}
          {item.mapDBAndAnalyticsEligible && (
            <Badge color="severity-medium">DB/Analytics</Badge>
          )}
          {!item.mapEligible &&
            !item.mapSapAndOracleEligible &&
            !item.mapDBAndAnalyticsEligible &&
            !item.isModernService && <Badge color="grey">Not Eligible</Badge>}
        </SpaceBetween>
      ),
      sortingField: "eligibility",
      sortingComparator: (a: ServiceItem, b: ServiceItem) =>
        getEligibilityString(a).localeCompare(getEligibilityString(b)),
    },
    {
      id: "region",
      header: "Region",
      cell: (item: ServiceItem) => item.region || "N/A",
      sortingField: "region",
    },
    {
      id: "cost",
      header: "Monthly Cost",
      cell: (item: ServiceItem) => USDollar.format(item.serviceCost || 0),
      sortingField: "serviceCost",
    },
  ];

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={paginatedItems}
      loading={false}
      loadingText="Loading resources"
      sortingColumn={{
        sortingField: sortingField,
      }}
      sortingDescending={sortingDescending}
      onSortingChange={({ detail }) => {
        setSortingField(detail.sortingColumn.sortingField as SortingField);
        setSortingDescending(detail.isDescending ?? false);
      }}
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder="Find services"
          filteringAriaLabel="Filter services"
          onChange={({ detail }) => {
            setFilteringText(detail.filteringText);
            setCurrentPage(1);
          }}
          countText={`${filteredItems.length} matches`}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          pagesCount={Math.ceil(sortedItems.length / preferences.pageSize)}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>No data available</b>
          <Box variant="p" color="inherit">
            There are no eligible services to display
          </Box>
        </Box>
      }
      enableKeyboardNavigation
      stickyHeader
      wrapLines
      variant="full-page"
    />
  );
};

export default EligibleServicesTable;
